/**
 * Centralized Actions Registry
 *
 * Defines all standard action buttons with their icon, testId, tooltip, purpose, and intent.
 * Organized by PURPOSE — what the user is trying to accomplish.
 *
 * Purpose determines intent (color). Verbs within a purpose are the SAME THING:
 * "create" is not different from "add" — both bring something new into existence,
 * both are primary. The only differences are label text and icon.
 *
 * Two independent axes control how a button looks:
 * - Purpose → Intent → Color (what the action does semantically)
 * - Rendering Mode → Prominence (where the button appears)
 *
 * Adding new actions:
 *   1. Identify the PURPOSE (which section does it belong to?)
 *   2. Add the testId to src/test/testIds.ts ui.actions
 *   3. Add the action definition here in the correct purpose section
 *   4. Intent is derived from purpose — don't set it manually
 *
 * Usage:
 *   import { Actions } from '@/theme';
 *   <ActionButton action={Actions.archive} onClick={handleArchive} />
 */

import type { CSSProperties, ComponentType } from 'react';
import { Icons } from './icons';
import { testIds } from '@/test/testIds';
import { buttonLabels } from '@/constants/buttonLabels';

// ============================================
// Types
// ============================================

/**
 * Purpose categories for actions.
 * Purpose determines intent — all actions in a purpose share the same visual treatment.
 */
export type ActionPurpose =
  | 'create'
  | 'distribute'
  | 'modify'
  | 'destroy'
  | 'restore'
  | 'affirm'
  | 'dismiss'
  | 'navigate'
  | 'select'
  | 'find'
  | 'recover'
  | 'authenticate';

/**
 * Intent types for action styling.
 * Maps to intentColors in intent.ts for consistent semantic coloring.
 */
export type ActionIntent = 'primary' | 'confirm' | 'destructive' | 'neutral';

/**
 * Purpose → Intent mapping. Every action inherits intent from its purpose.
 */
export const PURPOSE_INTENT: Record<ActionPurpose, ActionIntent> = {
  create: 'primary',
  distribute: 'primary',
  modify: 'neutral',
  destroy: 'destructive',
  restore: 'confirm',
  affirm: 'confirm',
  dismiss: 'neutral',
  navigate: 'neutral',
  select: 'neutral',
  find: 'neutral',
  recover: 'neutral',
  authenticate: 'primary',
};

/**
 * Definition for a standard action button.
 */
export interface ActionDefinition {
  /** Semantic test ID for testing */
  testId: string;
  /** Icon component to render */
  icon: ComponentType<{ style?: CSSProperties }>;
  /** Default tooltip text */
  tooltip: string;
  /** Purpose category — determines intent */
  purpose: ActionPurpose;
  /** Semantic intent for styling (derived from purpose) */
  intent: ActionIntent;
}

/** Helper to define an action with purpose-derived intent */
function action(
  testId: string,
  icon: ComponentType<{ style?: CSSProperties }>,
  tooltip: string,
  purpose: ActionPurpose,
): ActionDefinition {
  return { testId, icon, tooltip, purpose, intent: PURPOSE_INTENT[purpose] };
}

// ============================================
// Actions Registry
// ============================================

export const Actions = {
  // ============================================
  // Purpose: Create (primary) — bring something new into existence
  // ============================================

  create: action(testIds.ui.actions.create, Icons.create, 'Create', 'create'),
  add: action(testIds.ui.actions.add, Icons.add, 'Add', 'create'),
  addImage: action(testIds.ui.actions.addImage, Icons.addPhoto, 'Add Image', 'create'),
  upload: action(testIds.ui.actions.upload, Icons.upload, 'Upload', 'create'),
  import: action(testIds.ui.actions.import, Icons.upload, 'Import', 'create'),
  generate: action(testIds.ui.actions.generate, Icons.autoAwesome, 'Generate', 'create'),
  download: action(testIds.ui.actions.download, Icons.fileOpenOutlined, 'Export', 'create'),
  certificate: action(testIds.ui.actions.certificate, Icons.componentCertificate, 'Download Certificate', 'create'),

  // ============================================
  // Purpose: Distribute (primary) — make content reach others
  // ============================================

  publish: action(testIds.ui.actions.publish, Icons.publish, 'Publish', 'distribute'),
  send: action(testIds.ui.actions.send, Icons.send, 'Send', 'distribute'),
  invite: action(testIds.ui.actions.invite, Icons.invite, buttonLabels.neutral.remind, 'distribute'),
  schedule: action(testIds.ui.actions.schedule, Icons.schedule, 'Schedule', 'distribute'),
  share: action(testIds.ui.actions.share, Icons.share, 'Share', 'distribute'),
  grant: action(testIds.ui.actions.grant, Icons.premium, 'Grant Premium Items', 'distribute'),

  // ============================================
  // Purpose: Modify (neutral) — change something that exists
  // ============================================

  manage: action(testIds.ui.actions.manage, Icons.handyman, 'Manage', 'modify'),
  edit: action(testIds.ui.actions.edit, Icons.edit, 'Edit', 'modify'),
  revise: action(testIds.ui.actions.revise, Icons.edit, 'Revise', 'modify'),
  copy: action(testIds.ui.actions.copy, Icons.copy, 'Copy', 'modify'),
  recreate: action(testIds.ui.actions.recreate, Icons.cached, 'Recreate', 'modify'),
  merge: action(testIds.ui.actions.merge, Icons.merge, 'Merge', 'modify'),
  // #234: re-point a contact method from the contact that holds it onto this
  // one. A modification of both contacts, not a destruction — the holder keeps
  // all of its work and its history; only the address changes hands.
  move: action(testIds.ui.actions.move, Icons.forward, 'Move', 'modify'),
  swap: action(testIds.ui.actions.swap, Icons.switchContext, 'Swap', 'modify'),
  labels: action(testIds.ui.actions.labels, Icons.label, 'Manage Labels', 'modify'),
  owner: action(testIds.ui.actions.owner, Icons.accountBox, 'Change Owner', 'modify'),
  user: action(testIds.ui.actions.user, Icons.verified, 'Send Login Link', 'modify'),
  cropImage: action(testIds.ui.actions.cropImage, Icons.image, 'Crop Image', 'modify'),
  attributes: action(testIds.ui.actions.attributes, Icons.tune, 'Set Attributes', 'modify'),
  premiumToggle: action(testIds.ui.actions.premiumToggle, Icons.premium, 'Toggle Premium Status', 'modify'),
  update: action(testIds.ui.actions.update, Icons.edit, 'Update', 'modify'),

  // ============================================
  // Purpose: Destroy (destructive) — remove or disable
  // ============================================

  delete: action(testIds.ui.actions.delete, Icons.delete, 'Delete', 'destroy'),
  remove: action(testIds.ui.actions.remove, Icons.delete, 'Remove', 'destroy'),
  removeImage: action(testIds.ui.actions.removeImage, Icons.delete, 'Remove Image', 'destroy'),
  archive: action(testIds.ui.actions.archive, Icons.archive, 'Archive', 'modify'),
  unassign: action(testIds.ui.actions.unassign, Icons.personRemove, 'Unassign', 'destroy'),
  clear: action(testIds.ui.actions.clear, Icons.clear, 'Clear', 'destroy'),
  reset: action(testIds.ui.actions.reset, Icons.replay, 'Reset', 'destroy'),
  disconnect: action(testIds.ui.actions.disconnect, Icons.linkOff, 'Disconnect', 'destroy'),
  deactivate: action(testIds.ui.actions.deactivate, Icons.deactivate, 'Deactivate', 'destroy'),

  // ============================================
  // Purpose: Restore (confirm) — reverse a destruction
  // ============================================

  unarchive: action(testIds.ui.actions.unarchive, Icons.unarchive, 'Unarchive', 'restore'),
  activate: action(testIds.ui.actions.activate, Icons.activate, 'Activate', 'restore'),

  // ============================================
  // Purpose: Affirm (confirm) — proceed with a decision
  // ============================================

  confirm: action(testIds.ui.actions.confirm, Icons.confirm, 'Confirm', 'affirm'),
  save: action(testIds.ui.actions.save, Icons.save, 'Save', 'affirm'),
  submit: action(testIds.ui.actions.submit, Icons.confirm, 'Submit', 'affirm'),
  complete: action(testIds.ui.actions.complete, Icons.success, 'Complete', 'affirm'),
  resolve: action(testIds.ui.actions.resolve, Icons.success, 'Resolve', 'affirm'),
  done: action(testIds.ui.actions.done, Icons.confirm, 'Done', 'affirm'),
  apply: action(testIds.ui.actions.apply, Icons.confirm, 'Apply', 'affirm'),
  verify: action(testIds.ui.actions.verify, Icons.verified, 'Verify', 'affirm'),

  // ============================================
  // Purpose: Dismiss (neutral) — decline, exit, go back
  // ============================================

  cancel: action(testIds.ui.actions.cancel, Icons.cancel, 'Cancel', 'dismiss'),
  close: action(testIds.ui.actions.close, Icons.close, 'Close', 'dismiss'),
  back: action(testIds.ui.actions.back, Icons.back, 'Back', 'dismiss'),

  // ============================================
  // Purpose: Navigate (neutral) — move through content
  // ============================================

  next: action(testIds.ui.actions.next, Icons.chevronRight, 'Next', 'navigate'),
  prev: action(testIds.ui.actions.prev, Icons.chevronLeft, 'Previous', 'navigate'),
  expand: action(testIds.ui.actions.expand, Icons.expandMore, 'Expand', 'navigate'),
  collapse: action(testIds.ui.actions.collapse, Icons.expandLess, 'Collapse', 'navigate'),
  view: action(testIds.ui.actions.view, Icons.preview, 'View', 'navigate'),
  preview: action(testIds.ui.actions.preview, Icons.preview, 'Preview', 'navigate'),
  menu: action(testIds.ui.actions.menu, Icons.menu, 'Menu', 'navigate'),
  settings: action(testIds.ui.actions.settings, Icons.settings, 'Settings', 'navigate'),
  history: action(testIds.ui.actions.history, Icons.history, 'History', 'navigate'),
  open: action(testIds.ui.actions.open, Icons.openInNew, 'Open', 'navigate'),
  browse: action(testIds.ui.actions.browse, Icons.folderOpen, 'Browse', 'navigate'),
  home: action(testIds.ui.actions.home, Icons.home, 'Home', 'navigate'),
  showSummary: action(testIds.ui.actions.showSummary, Icons.summary, 'Show Summary', 'navigate'),

  // ============================================
  // Purpose: Select (neutral) — choose items in a list
  // ============================================

  selectAll: action(testIds.ui.actions.selectAll, Icons.selectAll, 'Select All', 'select'),
  deselectAll: action(testIds.ui.actions.deselectAll, Icons.deselect, 'Deselect All', 'select'),

  // ============================================
  // Purpose: Find (neutral) — locate, filter, organize
  // ============================================

  search: action(testIds.ui.actions.search, Icons.search, 'Search', 'find'),
  filter: action(testIds.ui.actions.filter, Icons.filter, 'Save Filter', 'find'),
  sort: action(testIds.ui.actions.sort, Icons.sort, 'Sort', 'find'),
  categorize: action(testIds.ui.actions.categorize, Icons.label, 'Categorize', 'find'),

  // ============================================
  // Purpose: Recover (neutral) — handle failures
  // ============================================

  retry: action(testIds.ui.actions.retry, Icons.replay, 'Retry', 'recover'),
  refresh: action(testIds.ui.actions.refresh, Icons.refresh, 'Refresh', 'recover'),
  resend: action(testIds.ui.actions.resend, Icons.replay, 'Resend', 'recover'),

  // ============================================
  // Purpose: Authenticate (primary) — identity flows
  // ============================================

  signIn: action(testIds.ui.actions.signIn, Icons.login, 'Sign In', 'authenticate'),
  signUp: action(testIds.ui.actions.signUp, Icons.invite, 'Sign Up', 'authenticate'),
  logOut: action(testIds.ui.actions.logOut, Icons.signOut, 'Log Out', 'authenticate'),
  resetPassword: action(testIds.ui.actions.resetPassword, Icons.lockReset, 'Reset Password', 'authenticate'),
} as const;

/**
 * Type for action keys
 */
export type ActionType = keyof typeof Actions;
